import { createApi } from '@reduxjs/toolkit/query/react';

import { get } from 'api';
import { authApiBaseURL, baseQuery } from './queries';

// On a successful enroll/enable, flip the cached MFA status to enabled immediately. The
// `mfa` tag invalidation still refetches the true state, but patching the cache now closes
// the window where the onboarding/ally gates read a stale enabled:false and bounce the user
// back to the MFA step they just completed (the refetch then confirms enabled:true).
const flipMfaStatusEnabledOnSuccess = async (_arg, { dispatch, queryFulfilled }) => {
  try {
    await queryFulfilled;
    dispatch(
      userApi.util.updateQueryData('getMfaStatus', undefined, (draft) => {
        // eslint-disable-next-line no-param-reassign
        if (draft) draft.enabled = true;
      }),
    );
  } catch {
    // Enrollment failed — leave the cache alone; the user stays gated as expected.
  }
};

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery,
  tagTypes: ['user', 'mfa'],
  endpoints: (builder) => ({
    signIn: builder.mutation({
      query: (data) => ({
        url: '/login',
        method: 'POST',
        data,
        options: { baseURL: authApiBaseURL },
      }),
      invalidatesTags: ['user'],
    }),
    verifyMfa: builder.mutation({
      query: (data) => ({
        url: '/auth/mfa/verify',
        method: 'POST',
        data,
        options: { baseURL: authApiBaseURL },
      }),
      invalidatesTags: ['user'],
    }),
    // Forced-enrollment flow (network requires MFA): exchange a setup_token for a QR/secret,
    // then enroll. These are public auth endpoints — no session yet.
    setupMfaEnrollment: builder.mutation({
      query: (data) => ({
        url: '/auth/mfa/setup',
        method: 'POST',
        data,
        options: { baseURL: authApiBaseURL },
      }),
    }),
    enrollMfa: builder.mutation({
      query: (data) => ({
        url: '/auth/mfa/enroll',
        method: 'POST',
        data,
        options: { baseURL: authApiBaseURL },
      }),
      // Invalidate `mfa` too so useGetMfaStatusQuery doesn't keep serving a pre-enrollment
      // enabled:false payload after forced enrollment (matches voluntary enableMfa).
      invalidatesTags: ['user', 'mfa'],
      onQueryStarted: flipMfaStatusEnabledOnSuccess,
    }),
    getMfaStatus: builder.query({
      query: () => ({ url: '/users/me/mfa' }),
      providesTags: ['mfa'],
    }),
    setupMfa: builder.mutation({
      query: () => ({ url: '/users/me/mfa/setup', method: 'POST' }),
    }),
    enableMfa: builder.mutation({
      query: (data) => ({ url: '/users/me/mfa/enable', method: 'POST', data }),
      invalidatesTags: ['mfa'],
      onQueryStarted: flipMfaStatusEnabledOnSuccess,
    }),
    disableMfa: builder.mutation({
      query: (data) => ({ url: '/users/me/mfa', method: 'DELETE', data }),
      invalidatesTags: ['mfa'],
    }),
    regenerateBackupCodes: builder.mutation({
      query: (data) => ({ url: '/users/me/mfa/backup_codes', method: 'POST', data }),
    }),
    signUp: builder.mutation({
      query: (data) => ({
        url: '/users',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['user'],
    }),
    getUser: builder.query({
      query: () => ({ url: '/users/me' }),
      providesTags: ['user'],
    }),
    updateUser: builder.mutation({
      query: (data) => ({ url: `/users/me`, method: 'PUT', data }),
      invalidatesTags: ['user'],
    }),
    confirmUser: builder.mutation({
      queryFn: async ({ confirmationToken }) => {
        const [userResponse] = await Promise.allSettled([
          get(`/users/confirmation`, {
            baseURL: authApiBaseURL,
            params: {
              confirmationToken,
            },
          }),
        ]);

        const errors = userResponse.reason?.response?.data?.errors ?? [];

        if (errors.includes('token is invalid')) {
          return { error: 'invalidToken' };
        }

        const [meResponse] = await Promise.allSettled([
          get(`/users/me`, {
            baseURL: import.meta.env.VITE_API_BASE_URL,
          }),
        ]);

        if (meResponse.status === 'rejected') {
          return {
            error: {
              message: 'Failed to get user',
            },
          };
        }

        const user = meResponse.value.data;

        return {
          data: user,
          error: null,
        };
      },
    }),
  }),
});

export const {
  useSignUpMutation,
  useGetUserQuery,
  useLazyGetUserQuery,
  useUpdateUserMutation,
  useSignInMutation,
  useConfirmUserMutation,
  useVerifyMfaMutation,
  useSetupMfaEnrollmentMutation,
  useEnrollMfaMutation,
  useGetMfaStatusQuery,
  useSetupMfaMutation,
  useEnableMfaMutation,
  useDisableMfaMutation,
  useRegenerateBackupCodesMutation,
} = userApi;
